import { use, useState } from 'react';
import {
  Table,
  Stack,
  Text,
  Alert,
  Box,
  Paper,
  Modal,
  Button,
  Group,
  SegmentedControl,
  List,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { LoansContext } from '@/context/loans-context-definition';
import { PlanType, PLAN_LABELS } from '@/types/planTypes';
import type { AllPlanSummaries, PlanSummaryData } from '@/types/planSummaryTypes';
import type {
  AllPlansResult,
  LoanResults,
  MonthlyAmortizationDetail,
  AnnualAmortizationDetail,
} from '@/types/loanCalculationTypes';
import { formatCurrency, formatDateMMMMYYYY } from '@/utils/formatters';

// Copied and adapted MonthlyDetailsTable from calculation-debugger.tsx
function MonthlyDetailsTable({
  details,
  planLabel,
}: {
  details: MonthlyAmortizationDetail[];
  planLabel: string;
}) {
  const [viewMode, setViewMode] = useState<'monthly' | 'annual'>('monthly');

  if (details.length === 0) {
    return <Text>No monthly details available for {planLabel}.</Text>;
  }

  const monthlyRows = details.map((month) => (
    <Table.Tr key={`month-${String(month.monthIndex)}`}>
      <Table.Td>{month.dateString}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.discretionaryIncome, '-')}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.startingPrincipal, '-', true)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.accruedInterestForMonth, '-', true)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.calculatedPayment, '-', true)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.principalPayment, '-', true)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.interestPayment, '-', true)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(month.endingPrincipal, '-', true)}</Table.Td>
      <Table.Td ta="right">
        {formatCurrency(month.endingNonCapitalizedInterest, '-', true)}
      </Table.Td>
    </Table.Tr>
  ));

  const annualDetails: AnnualAmortizationDetail[] = [];
  if (viewMode === 'annual') {
    const detailsByBlock: Record<number, MonthlyAmortizationDetail[]> = {};
    details.forEach((month) => {
      const blockIndex = Math.floor(month.monthIndex / 12);
      detailsByBlock[blockIndex] = detailsByBlock[blockIndex] ?? [];
      detailsByBlock[blockIndex].push(month);
    });

    Object.keys(detailsByBlock)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((blockIndex) => {
        const blockMonths = detailsByBlock[blockIndex];
        if (blockMonths.length === 0) return;

        const firstMonth = blockMonths[0];
        const lastMonth = blockMonths[blockMonths.length - 1];

        const annualSum: AnnualAmortizationDetail = {
          year: blockIndex + 1, // 1-indexed year
          discretionaryIncome: firstMonth.discretionaryIncome ?? null,
          startingPrincipal: firstMonth.startingPrincipal,
          accruedInterestForYear: blockMonths.reduce(
            (sum, m) => sum + m.accruedInterestForMonth,
            0
          ),
          calculatedPayment: blockMonths.reduce((sum, m) => sum + m.calculatedPayment, 0),
          principalPayment: blockMonths.reduce((sum, m) => sum + m.principalPayment, 0),
          interestPayment: blockMonths.reduce((sum, m) => sum + m.interestPayment, 0),
          endingPrincipal: lastMonth.endingPrincipal,
          endingInterest: lastMonth.endingNonCapitalizedInterest,
        };
        annualDetails.push(annualSum);
      });
  }

  const annualRows = annualDetails.map((yearBlock) => (
    <Table.Tr key={`year-${String(yearBlock.year)}`}>
      <Table.Td>{`Year ${String(yearBlock.year)}`}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.discretionaryIncome, '-')}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.startingPrincipal)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.accruedInterestForYear)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.calculatedPayment / 12)} (avg)</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.principalPayment)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.interestPayment)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.endingPrincipal)}</Table.Td>
      <Table.Td ta="right">{formatCurrency(yearBlock.endingInterest)}</Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack>
      <Group justify="flex-end">
        <SegmentedControl
          value={viewMode}
          onChange={(value: string) => {
            setViewMode(value as 'monthly' | 'annual');
          }}
          data={[
            { label: 'Monthly view', value: 'monthly' },
            { label: 'Annual view', value: 'annual' },
          ]}
        />
      </Group>
      <Box style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <Table stickyHeader highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{viewMode === 'monthly' ? 'Date' : 'Year'}</Table.Th>
              <Table.Th ta="right">Adj income</Table.Th>
              <Table.Th ta="right">Start principal</Table.Th>
              <Table.Th ta="right">
                {viewMode === 'monthly' ? 'Accrued interest' : 'Interest accrued this year'}
              </Table.Th>
              <Table.Th ta="right">Monthly payment</Table.Th>
              <Table.Th ta="right">
                {viewMode === 'monthly' ? 'Principal' : 'Principal paid this year'}
              </Table.Th>
              <Table.Th ta="right">
                {viewMode === 'monthly' ? 'Interest' : 'Interest paid this year'}
              </Table.Th>
              <Table.Th ta="right">End principal</Table.Th>
              <Table.Th ta="right">End interest</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{viewMode === 'monthly' ? monthlyRows : annualRows}</Table.Tbody>
        </Table>
      </Box>
    </Stack>
  );
}
// End of MonthlyDetailsTable

// Define the order of plans and their corresponding PlanType or a string label
const ORDERED_PLANS: Array<{ label: string; type: PlanType | string }> = [
  { label: PLAN_LABELS[PlanType.ICR], type: PlanType.ICR },
  { label: PLAN_LABELS[PlanType.OLD_IBR], type: PlanType.OLD_IBR },
  { label: PLAN_LABELS[PlanType.PAYE], type: PlanType.PAYE },
  { label: PLAN_LABELS[PlanType.NEW_IBR], type: PlanType.NEW_IBR },
  { label: PLAN_LABELS[PlanType.SAVE], type: PlanType.SAVE },
  { label: PLAN_LABELS[PlanType.ICR_PSLF], type: PlanType.ICR_PSLF },
  { label: PLAN_LABELS[PlanType.OLD_IBR_PSLF], type: PlanType.OLD_IBR_PSLF },
  { label: PLAN_LABELS[PlanType.PAYE_PSLF], type: PlanType.PAYE_PSLF },
  { label: PLAN_LABELS[PlanType.NEW_IBR_PSLF], type: PlanType.NEW_IBR_PSLF },
  { label: PLAN_LABELS[PlanType.SAVE_PSLF], type: PlanType.SAVE_PSLF },
  { label: PLAN_LABELS[PlanType.STANDARD_10_YEAR], type: PlanType.STANDARD_10_YEAR },
  { label: PLAN_LABELS[PlanType.GRADUATED], type: PlanType.GRADUATED },
  { label: PLAN_LABELS[PlanType.EXTENDED_25_YEAR], type: PlanType.EXTENDED_25_YEAR },
  { label: PLAN_LABELS[PlanType.EXTENDED_GRADUATED], type: PlanType.EXTENDED_GRADUATED },
  { label: '30Y fixed', type: '30Y_FIXED_CUSTOM' }, // Custom type for plans not in enum
  { label: PLAN_LABELS[PlanType.PRIVATE_REFINANCE], type: PlanType.PRIVATE_REFINANCE },
];

const COLUMN_HEADERS = [
  'Plan',
  'Y1 mo. payment',
  'Max mo. payment',
  'Amount forgiven',
  'Projected taxes owed',
  'Finish date',
  'Total payments',
  'Payments PV',
];

function getCombinedPlanData(
  planSummaries: AllPlanSummaries | null | undefined,
  planType: PlanType | string
): PlanSummaryData | null {
  if (!planSummaries || typeof planType !== 'string') {
    return null;
  }
  const isPlanTypeEnum = Object.values(PlanType).includes(planType as PlanType);
  if (isPlanTypeEnum) {
    const summary = planSummaries[planType as PlanType];
    return summary.combined; // Corrected: simpler null check for summary.combined
  }
  return null;
}

export function PlanSummaryDebugger() {
  const context = use(LoansContext);
  const [isLoanSelectionModalOpen, setIsLoanSelectionModalOpen] = useState(false);
  const [isMonthlyDetailModalOpen, setIsMonthlyDetailModalOpen] = useState(false);
  const [selectedPlanContext, setSelectedPlanContext] = useState<{
    planType: PlanType;
    planLabel: string;
    loanAwardId?: string;
  } | null>(null);
  const [loansForSelectedPlanType, setLoansForSelectedPlanType] = useState<AllPlansResult[]>([]);
  const [selectedLoanCalcResult, setSelectedLoanCalcResult] = useState<LoanResults | null>(null);

  if (!context) {
    return <Alert color="red">Loan context is not available.</Alert>;
  }

  const {
    scenarioPlanSummaries,
    scenarioResults,
    scenarioCalculationLoading,
    scenarioCalculationError,
    filteredLoans,
  } = context;

  const handleSummaryRowClick = (planType: PlanType | string, planLabel: string) => {
    if (
      typeof planType !== 'string' ||
      !Object.values(PlanType).includes(planType as PlanType) ||
      !scenarioResults
    ) {
      // If it's a custom type or no scenarioResults, do nothing (or show a message)
      console.log('Cannot show details for custom plan types or if no results exist:', planLabel);
      return;
    }
    const typedPlanType = planType as PlanType;
    const relevantLoans = scenarioResults.filter((loanPlanResult) =>
      loanPlanResult.results.some((res) => res.planName === typedPlanType)
    );

    setLoansForSelectedPlanType(relevantLoans);
    setSelectedPlanContext({ planType: typedPlanType, planLabel });
    setIsLoanSelectionModalOpen(true);
  };

  const handleLoanSelectionForDetails = (loanPlanResult: AllPlansResult) => {
    if (!selectedPlanContext) return;
    const specificLoanResult = loanPlanResult.results.find(
      (res) => res.planName === selectedPlanContext.planType
    );
    if (specificLoanResult) {
      setSelectedLoanCalcResult(specificLoanResult);
      setSelectedPlanContext((prev) =>
        prev ? { ...prev, loanAwardId: loanPlanResult.loanAwardId } : null
      );
      setIsLoanSelectionModalOpen(false);
      setIsMonthlyDetailModalOpen(true);
    }
  };

  if (scenarioCalculationLoading) {
    return (
      <Text ta="center" p="lg">
        Calculating...
      </Text>
    );
  }

  if (scenarioCalculationError) {
    return (
      <Alert title="Error" color="red" icon={<IconAlertCircle size={18} />} radius="md">
        {typeof scenarioCalculationError === 'string'
          ? scenarioCalculationError
          : 'An unknown error occurred.'}
      </Alert>
    );
  }

  if (!scenarioPlanSummaries) {
    return (
      <Alert title="No Summaries" icon={<IconAlertCircle size={18} />} radius="md">
        No plan summaries are available.
      </Alert>
    );
  }

  return (
    <>
      <Stack h="100%" gap="md">
        <Paper shadow="none" p="md" radius="md" style={{ flex: 1, overflow: 'hidden' }}>
          <Box style={{ overflow: 'auto', height: '100%' }}>
            <Table stickyHeader highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  {COLUMN_HEADERS.map((header) => (
                    <Table.Th key={header} ta={header === 'Plan' ? 'left' : 'right'}>
                      {header}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ORDERED_PLANS.map(({ label, type }) => {
                  const combinedData = getCombinedPlanData(scenarioPlanSummaries, type);
                  const isClickable =
                    typeof type === 'string' && Object.values(PlanType).includes(type as PlanType);

                  return (
                    <Table.Tr
                      key={label}
                      onClick={() => {
                        if (isClickable) {
                          handleSummaryRowClick(type, label);
                        }
                      }}
                      style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                      <Table.Td>{label}</Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.firstMonthlyPayment
                          ? formatCurrency(combinedData.firstMonthlyPayment)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.maxMonthlyPayment
                          ? formatCurrency(combinedData.maxMonthlyPayment)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.totalForgiven
                          ? formatCurrency(combinedData.totalForgiven)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.taxesOwedOnForgivenRaw
                          ? formatCurrency(combinedData.taxesOwedOnForgivenRaw)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.finishDate
                          ? formatDateMMMMYYYY(combinedData.finishDate)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.totalPayment
                          ? formatCurrency(combinedData.totalPayment)
                          : '-'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {combinedData?.totalLoanPaymentsDiscounted
                          ? formatCurrency(combinedData.totalLoanPaymentsDiscounted)
                          : '-'}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>
        </Paper>
      </Stack>

      {/* Loan Selection Modal */}
      <Modal
        opened={isLoanSelectionModalOpen}
        onClose={() => {
          setIsLoanSelectionModalOpen(false);
        }}
        title={
          selectedPlanContext
            ? `Select Loan for ${selectedPlanContext.planLabel} Details`
            : 'Select Loan'
        }
        size="lg"
        centered
      >
        {loansForSelectedPlanType.length > 0 ? (
          <List spacing="xs" size="sm" center>
            {loansForSelectedPlanType.map((loanPlanResult) => {
              // Find the specific loan details from filteredLoans to get a good description
              const loanDetail = filteredLoans.find(
                (fl) => fl['Loan Award ID'] === loanPlanResult.loanAwardId
              );
              const description = loanDetail
                ? `${loanDetail['Loan Type Group']} (${
                    loanDetail['Loan Award ID']
                  }) - ${formatCurrency(loanDetail['Total Outstanding'])} @ ${String(
                    loanDetail['Loan Interest Rate']
                  )}% ${loanPlanResult.borrower === 'partner' ? '(Partner)' : '(User)'}`
                : `${loanPlanResult.loanAwardId} ${
                    loanPlanResult.borrower === 'partner' ? '(Partner)' : '(User)'
                  }`;

              return (
                <List.Item key={loanPlanResult.loanAwardId}>
                  <Button
                    variant="subtle"
                    onClick={() => {
                      handleLoanSelectionForDetails(loanPlanResult);
                    }}
                  >
                    {description}
                  </Button>
                </List.Item>
              );
            })}
          </List>
        ) : (
          <Text>No specific loan calculation details found for this plan.</Text>
        )}
      </Modal>

      {/* Monthly Payment Details Modal */}
      <Modal
        opened={isMonthlyDetailModalOpen && !!selectedLoanCalcResult}
        onClose={() => {
          setIsMonthlyDetailModalOpen(false);
        }}
        title={
          selectedPlanContext && selectedLoanCalcResult
            ? `Monthly Details: ${PLAN_LABELS[selectedLoanCalcResult.planName]} for ${
                selectedPlanContext.loanAwardId
                  ? `Loan ${selectedPlanContext.loanAwardId}`
                  : selectedPlanContext.planLabel
              }`
            : 'Monthly Details'
        }
        fullScreen
        centered
      >
        {selectedLoanCalcResult && selectedPlanContext && (
          <MonthlyDetailsTable
            details={selectedLoanCalcResult.monthlyDetails}
            planLabel={selectedPlanContext.planLabel}
          />
        )}
      </Modal>
    </>
  );
}
